<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <h1 class="text-3xl text-center">
                Páginas personalizadas encontradas
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="$ability.hasPermission('blog:Create')"
                    color="success"
                    size="large"
                    @click="$router.push('/app/blogs/new')"
                >
                    <span class="hidden md:inline">Nueva página</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap">
            <div class="w-full mt-3">
                <common-filters
                    v-model="query"
                    :enabled-filters="$data.$enabledFilters"
                    :default-filters="$data.$defaultFilters"
                    @enter="reloadItems"
                />
                <div class="flex justify-center">
                    <app-button v-show="query" relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>
            <div
                class="flex flex-col lg:flex-row items-center justify-between w-full mt-3 gap-4"
            >
                <div
                    class="flex flex-wrap items-center justify-center lg:justify-start gap-2"
                >
                    <tooltip-button
                        v-show="
                            selected.length &&
                            $ability.hasPermission('blog:Create')
                        "
                        icon="🗂️"
                        message="Clonar página"
                        @click="cloneSelected"
                    />
                </div>

                <div
                    class="flex flex-wrap items-center justify-center lg:justify-end gap-3"
                >
                    <list-settings-button
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />
                    <tooltip-redirect
                        v-if="$ability.hasPermission('blog:Create')"
                        to="/app/blogs/new"
                        message="Nueva página"
                    />
                </div>
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-table
                ref="selectableTable"
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                select-mode="single"
                selectable
                outlined
                responsive
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @flex-selected="onRowSelected"
            >
                <template #cell(slug)="data">
                    <div class="flex items-center">
                        <router-link
                            :to="`/app/blogs/detail/${data.item.slug}`"
                            class="text-center btn btn-stack-primary p-1"
                        >
                            <div class="flex items-center">
                                <small class="ml-1">
                                    {{ data.item.active ? "🟢" : "⚪" }}
                                </small>
                                <small>
                                    {{ data.item.slug }}
                                </small>
                            </div>
                        </router-link>
                    </div>
                </template>
                <template #cell(title)="data">
                    <span class="text-center">
                        {{ data.item.title }}
                    </span>
                </template>
                <template #cell(summary)="data">
                    <span class="text-center">
                        {{ data.item.summary }}
                    </span>
                </template>
            </custom-table>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import CommonFilters from "@/components/utils/CommonFilters.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";
import TooltipRedirect from "@/components/utils/TooltipRedirect.vue";

export default {
    name: "BlogsIndex",
    components: { CustomTable, TooltipRedirect, TooltipButton, CommonFilters },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    data: () => ({
        query: {},
        keySearch: "",
        itemsCurrent: [],
        selected: [],
        $fields: [
            { key: "slug", label: "Slug", sortable: true },
            { key: "title", label: "Titulo", sortable: true },
            {
                key: "summary",
                label: "Descripción",
                sortable: false
            }
        ],
        $enabledFilters: ["keyword", "active"],
        $defaultFilters: ["keyword"],
        $urlPaginationKey: "page",
        $urlFilterSchema: [
            { key: "keyword", type: "string" },
            { key: "active", type: "boolean" }
        ],

        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        await verifyPagePermission("blog:List", "/app");
        this.listItems(true);
    },
    methods: {
        ...mapActions("blogs", ["listBlogsPaginated"]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listBlogsPaginated({
                    ...this.query,
                    complete,
                    itemsByPage: this.pageSize,
                    page: this.currentPage
                });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        clearSelected() {
            this.$refs.selectableTable.clearSelected();
            this.selected = [];
        },
        cloneSelected() {
            window
                .open(`/app/blogs/clone/${this.selected[0].slug}`, "_blank")
                .focus();
        }
    }
};
</script>
